//
// Exception noise that never originates in scite code.
//
// Two shapes of it bury real P1s in the Sentry and PostHog inboxes:
//
// 1. Messages that are always a browser limitation or a third-party script -
//    ResizeObserver loop notices, cross-origin "Script error." blackouts, the
//    extension and WebView bridges below.
//
// 2. Whole stacks belonging to code the embedding client injected into the page.
//    In-app WebView clients - the Google Search App (`GSA/`), the Meta in-app
//    browsers - run their own minified bridge inside every page they render, and
//    WKWebView attributes those frames to the host page's URL instead of to a
//    script of their own. The result reads like a first-party crash: in SCI-3891
//    a `Fk` <-> `Hk` recursion reported at `https://scite.ai/:224:63`, on a line
//    the 118-line document does not have, with the same coordinates on every
//    route. Nothing we serve defines those symbols, and there is no source map to
//    resolve them against.
//

const NOISE_MESSAGES = [
  'ResizeObserver loop', // browser limitation, not a fault in the observed code
  'Script error.', // cross-origin script with no CORS headers; carries no stack or message
  'Java object is gone', // Android WebView tearing down its postMessage bridge mid-page
  'window.webkit.messageHandlers', // iOS WebView bridge missing when a page outlives its host app view
  // Firefox iOS user scripts, also injected by Brave iOS (a Firefox iOS fork). WebKit
  // reports a bare `Can't find variable: __firefox__` with no `window.` prefix.
  '__firefox__',
  'zoteroConnector' // Zotero browser extension
]

/** True when a message is always third-party or browser noise, wherever it surfaces. */
export const isNoiseExceptionMessage = (message?: string | null): boolean => {
  if (!message) {
    return false
  }
  const lowered = message.toLowerCase()
  return NOISE_MESSAGES.some(noise => lowered.includes(noise.toLowerCase()))
}

//
// Clients that render our pages inside another app and inject their own bridge.
//
// `CriOS` (Chrome on iOS) joined this list in SCI-4381, reversing the original
// SCI-3891 stance that a browser the user chose shouldn't be treated as a
// wrapped view. SCI-4381 was 20 events across 17 users, 100% Chrome Mobile iOS,
// spread across unrelated pages (assistant, blog, search, ...), across three
// iOS versions and three Chrome versions, and zero on Mobile Safari or Firefox
// iOS. WebKit runs the same inline-script bytes on every iOS browser, so a
// genuine fault in the New Relic loader or the AWS WAF snippet would have shown
// up there too - it never did. That rules out "real bug in our own inline
// script, hidden by WebKit's sourceURL bug" and leaves Chrome's own injected
// bridge as the explanation: the same shape as GSA, just from Google's other
// iOS app.
//
// SCI-3961 named our inline scripts with `sourceURL` to remove that ambiguity,
// and it does - on Blink and Gecko. WebKit ignores the directive for inline
// `<script>` tags (it honours it for `eval` only), verified against WebKit 26.4,
// so on iOS our inline scripts are still attributed to the page. Every client
// above that runs on WebKit - `GSA`, `CriOS`, and the Meta apps on iOS - keeps
// that narrow blind spot; the Android ones no longer do.
//
// Most of these delimit their token from its version with a slash. Instagram
// uses a space - `Instagram 437.0.0.33.78 Android (35/15; ...)` - so it needs its
// own alternative. Requiring the leading version digit stops the looser
// separator from matching the word anywhere else in a user agent.
//
const WRAPPED_WEBVIEW_UA = /\b(?:(?:GSA|FBAN|FBAV|FB_IAB|Line|MicroMessenger|CriOS)\/|Instagram[/ ]\d)/

export const isWrappedWebViewClient = (userAgent?: string | null): boolean =>
  !!userAgent && WRAPPED_WEBVIEW_UA.test(userAgent)

//
// A frame attributed to a page on our own origin rather than to a script we
// serve. Our own inline scripts (the New Relic loader, the AWS WAF snippet) look
// the same way, which is why the caller must also be a wrapped WebView client
// before any of this counts as noise.
//
const isPageAttributedFrame = (filename: string | undefined | null, origin: string): boolean => {
  if (!filename) {
    return false
  }
  try {
    // Absolute only - a frame we cannot resolve to a URL is not evidence of anything.
    const url = new URL(filename)
    return url.origin === origin && !/\.[cm]?js$/i.test(url.pathname)
  } catch {
    return false
  }
}

/**
 * True when every frame belongs to a script the embedding client injected, so
 * there is no scite code anywhere in the stack to act on.
 */
export const isInjectedClientStack = (
  filenames: (string | undefined | null)[],
  origin: string,
  userAgent?: string | null
): boolean =>
  isWrappedWebViewClient(userAgent) &&
  filenames.length > 0 &&
  filenames.every(filename => isPageAttributedFrame(filename, origin))

//
// Extensions that wrap XHR leave our own frames further down the stack, so only the frame that
// threw decides. Chrome writes frames as `at fn (url)`, Firefox and Safari as `fn@url`. Safari
// 16.4+ masks extension script URLs as `webkit-masked-url://hidden/`.
//
const STACK_FRAME = /^\s*at\s|^[^\s(]*@/
const STACK_FRAME_URL = /[a-z-]+:\/\/[^\s)]+/
const EXTENSION_URL = /^(?:(?:chrome|moz|safari-web)-extension|webkit-masked-url):\/\//

/** True when the error was thrown inside a browser extension's script. */
export const isExtensionStack = (stack?: string | null): boolean => {
  const throwingFrame = stack?.split('\n').find(line => STACK_FRAME.test(line))
  const url = throwingFrame?.match(STACK_FRAME_URL)?.[0]
  return !!url && EXTENSION_URL.test(url)
}

//
// `/_Incapsula_Resource` is Imperva's bot-detection script (SCI-3991). When it is answered
// with an HTML error page instead of JavaScript, the browser throws `Unexpected token '<'`
// at 1:1 of that URL (SCI-5026: datacenter-IP automation). Matching on frames rather than
// the message keeps a real `Unexpected token '<'` from our own code reporting.
//
const IMPERVA_RESOURCE = /\/_Incapsula_Resource(?:[/?#]|$)/

/** True when every frame belongs to Imperva's injected script. */
export const isImpervaStack = (filenames: (string | undefined | null)[]): boolean =>
  filenames.length > 0 && filenames.every(filename => !!filename && IMPERVA_RESOURCE.test(filename))
